import type { PostgrestError } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import { listExercises } from "@/lib/api/exercises";
import { listBundles } from "@/lib/api/quarterlyBundles";
import type { VideoRow, VideoType, BodyRegionFocus } from "@/types/database";

/** User-friendly message for RLS / permission failures on write paths. */
export function formatSupabaseAccessError(err: unknown): string {
  const e = err as PostgrestError | Error | null;
  if (e && typeof e === "object" && "code" in e) {
    const pe = e as PostgrestError;
    if (pe.code === "42501" || pe.message?.toLowerCase().includes("permission")) {
      return "Permission denied. HQ users with Content access can create or change videos.";
    }
    if (pe.message) {
      const m = pe.message;
      if (/\bschema cache\b/i.test(m)) {
        return `${m} Run pending Supabase migrations for this project (see supabase/migrations), then retry.`;
      }
      return m;
    }
  }
  if (err instanceof Error) return err.message;
  return "Request failed";
}

export interface ListVideosFilters {
  type?: VideoType;
  organization_id?: string | null;
  /** Substring match on title (case-insensitive). */
  search?: string;
  /** Filter by body_region tag (F2L-4). */
  body_region?: BodyRegionFocus;
  /**
   * When set: videos for this org OR shared catalog (`organization_id` is null).
   * Use for bundle intro picker so org-scoped and generic uploads both appear.
   * Mutually exclusive with plain `organization_id` — this branch takes precedence.
   */
  matching_organization_catalog_including_shared?: string;
  /** When true, exclude rows without a Cloudflare Stream id (not yet playable). */
  require_cloudflare_stream_id?: boolean;
}

/**
 * List videos visible under RLS (F2L-5):
 * - HQ superadmin / content admin: all rows (incl. inactive)
 * - Org members: active shared (`organization_id` null) + their org's videos
 *
 * Prefer `matching_organization_catalog_including_shared` for pickers so the
 * query intent matches the policy (shared ∪ this org).
 */
export async function listVideos(filters?: ListVideosFilters): Promise<VideoRow[]> {
  let q = supabase.from("videos").select("*").order("created_at", { ascending: false });
  if (filters?.type) q = q.eq("type", filters.type);

  if (typeof filters?.matching_organization_catalog_including_shared === "string") {
    const oid = filters.matching_organization_catalog_including_shared.trim();
    if (oid.length > 0) {
      q = q.or(`organization_id.eq.${oid},organization_id.is.null`);
    }
  } else if (filters?.organization_id === null) {
    q = q.is("organization_id", null);
  } else if (typeof filters?.organization_id === "string") {
    q = q.eq("organization_id", filters.organization_id);
  }

  if (filters?.require_cloudflare_stream_id) {
    q = q.not("cloudflare_stream_id", "is", null);
  }

  if (filters?.body_region) {
    q = q.eq("body_region", filters.body_region);
  }

  if (filters?.search?.trim()) {
    const term = filters.search.trim().replace(/%/g, "");
    q = q.or(`title.ilike.%${term}%,catalog_label.ilike.%${term}%`);
  }
  const { data, error } = await q;
  if (error) {
    if (import.meta.env.DEV) console.error("[listVideos]", error);
    throw new Error(formatSupabaseAccessError(error));
  }
  return (data ?? []) as VideoRow[];
}

export async function getVideo(id: string): Promise<VideoRow | null> {
  const { data, error } = await supabase.from("videos").select("*").eq("id", id).maybeSingle();
  if (error) {
    if (import.meta.env.DEV) console.error("[getVideo]", error);
    throw new Error(formatSupabaseAccessError(error));
  }
  return (data ?? null) as VideoRow | null;
}

export interface CreateVideoInput {
  title: string;
  type: VideoType;
  cloudflare_stream_id: string;
  duration_seconds: number | null;
  organization_id: string | null;
  created_by: string | null;
  thumbnail_url?: string | null;
  is_active?: boolean;
  body_region?: BodyRegionFocus | null;
  catalog_label?: string | null;
}

export async function createVideo(input: CreateVideoInput): Promise<VideoRow> {
  const payload = {
    title: input.title,
    type: input.type,
    cloudflare_stream_id: input.cloudflare_stream_id,
    duration_seconds: input.duration_seconds,
    organization_id: input.organization_id,
    created_by: input.created_by,
    ...(input.thumbnail_url != null && { thumbnail_url: input.thumbnail_url }),
    ...(input.is_active != null && { is_active: input.is_active }),
    ...(input.body_region !== undefined && { body_region: input.body_region }),
    ...(input.catalog_label !== undefined && { catalog_label: input.catalog_label }),
  };
  const { data, error } = await supabase
    .from("videos")
    .insert(payload as never)
    .select()
    .single();
  if (error) {
    if (import.meta.env.DEV) console.error("[createVideo]", error);
    throw new Error(formatSupabaseAccessError(error));
  }
  return data as VideoRow;
}

export async function updateVideo(
  id: string,
  patch: Partial<
    Pick<
      VideoRow,
      | "title"
      | "type"
      | "thumbnail_url"
      | "duration_seconds"
      | "organization_id"
      | "is_active"
      | "body_region"
      | "catalog_label"
    >
  >,
): Promise<VideoRow> {
  const { data, error } = await supabase
    .from("videos")
    .update(patch as never)
    .eq("id", id)
    .select()
    .single();
  if (error) {
    if (import.meta.env.DEV) console.error("[updateVideo]", error);
    throw new Error(formatSupabaseAccessError(error));
  }
  return data as VideoRow;
}

export async function deleteVideo(id: string): Promise<void> {
  const { error } = await supabase.from("videos").delete().eq("id", id);
  if (error) {
    if (import.meta.env.DEV) console.error("[deleteVideo]", error);
    throw new Error(formatSupabaseAccessError(error));
  }
}

/** Exercises and quarterly bundles referencing this video — blocks safe delete in UI when non-zero. */
export async function getVideoUsageCounts(videoId: string): Promise<{
  exerciseCount: number;
  introBundleCount: number;
}> {
  const [ex, introBundles, lineupBundles] = await Promise.all([
    listExercises({ video_id: videoId }),
    listBundles({ intro_video_id: videoId }),
    listBundles({ lineup_video_id: videoId }),
  ]);
  const bundleIds = new Set([
    ...introBundles.map((b) => b.id),
    ...lineupBundles.map((b) => b.id),
  ]);
  return { exerciseCount: ex.length, introBundleCount: bundleIds.size };
}
